/**
 * Real LCS (longest common subsequence) line-diff algorithm, extracted
 * from DiffChecker.tsx so Compare PDF uses the exact same tested logic
 * rather than a second implementation -- the same portability principle
 * behind codeGeneration.ts (shared barcode/QR logic) earlier.
 */

export type DiffLine = { type: "same" | "add" | "remove"; text: string };

// Guards against the O(n*m) DP table blowing up on pathologically large
// input, which would freeze the browser tab rather than fail gracefully.
export const MAX_DIFF_LINES = 3000;

export function diffLines(a: string[], b: string[]): DiffLine[] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const result: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      result.push({ type: "same", text: a[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      result.push({ type: "remove", text: a[i] });
      i++;
    } else {
      result.push({ type: "add", text: b[j] });
      j++;
    }
  }
  while (i < n) { result.push({ type: "remove", text: a[i] }); i++; }
  while (j < m) { result.push({ type: "add", text: b[j] }); j++; }
  return result;
}
